import { Component, type ComponentChildren } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'preact/hooks';
import { cellsToPx, clampSize, rowsToPx, gridMetrics, normalizeLauncherGrid, type GridMetrics, type WidgetInstance, type ZoneName } from '@frame/protocol';
import { RuntimeContext, WidgetContext, useMode } from '@frame/sdk/tv';
import type { Runtime } from './runtime.ts';
import { focusFirst } from './nav.ts';
import { fitWidget, watchWidget } from './widgetFit.ts';

class Boundary extends Component<{ rt: Runtime; widget: WidgetInstance; children: ComponentChildren }, { error?: string }> {
  state: { error?: string } = {};
  componentDidCatch(e: Error) {
    this.props.rt.log('error', `${this.props.widget.type} (${this.props.widget.id}) crashed: ${e.stack || e.message}`, this.props.widget.type.split('/')[0]);
    this.setState({ error: e.message });
  }
  render() {
    if (this.state.error) return this.props.rt.preview ? <div class="fl-widget-error">{this.props.widget.type}: {this.state.error}</div> : null;
    return this.props.children;
  }
}

/** Below this many px a tile counts as small: a dot (both sides) or a bar (height). The default 122 px grid gives dot 1x1, bar Nx1, card taller. */
const SMALL_PX = 150;

function WidgetView({ rt, w, m }: { rt: Runtime; w: WidgetInstance; m?: GridMetrics }) {
  const [module, name] = w.type.split('/');
  const Comp = rt.widgets.get(module)?.[name];
  // A stored layout can be wider than the current column count (columns were reduced later): shrink it to fit.
  const l = m ? { ...(w.layout ?? { w: 3, h: 1 }), ...clampSize(w.layout ?? { w: 3, h: 1 }, m.cols) } : undefined;
  // Real box in px, and the design-space size the widget sees (see widgetFit.ts): the same on any grid density.
  const geo = l && m ? { W: cellsToPx(m, l.w), H: rowsToPx(m, l.h), k: m.scale } : undefined;
  const size = useMemo(() => {
    if (!l || !geo) return undefined;
    const width = geo.W / geo.k;
    const height = geo.H / geo.k;
    return { w: l.w, h: l.h, width, height, scale: geo.k, shape: height < SMALL_PX ? (width < SMALL_PX ? 'dot' : 'bar') : 'card' } as const;
  }, [l?.w, l?.h, geo?.W, geo?.H, geo?.k]);
  const el = useRef<HTMLDivElement>(null);
  const cur = useRef(geo);
  cur.current = geo;
  // Lay the widget out in design space and shrink it if its content overflows; again on every content change.
  useLayoutEffect(() => {
    const node = el.current;
    if (!node) return;
    return watchWidget(node, () => cur.current ?? { W: node.offsetWidth, H: node.offsetHeight, k: 1 });
  }, [!!Comp, rt.bundles[module]?.js]); // a new bundle hash remounts the element (see Boundary key)
  useLayoutEffect(() => {
    if (el.current && geo) fitWidget(el.current, geo.W, geo.H, geo.k);
  }, [geo?.W, geo?.H, geo?.k, rt.bundles[module]?.js]);
  // On the grid: a fixed position (kept inside the columns) or auto-placement (list order, gaps get filled).
  const x = l && m && l.x != null ? Math.max(0, Math.min(l.x, m.cols - l.w)) : undefined;
  const style = l
    ? { gridColumn: x != null ? `${x + 1} / span ${l.w}` : `span ${l.w}`, gridRow: l.y != null ? `${l.y + 1} / span ${l.h}` : `span ${l.h}` }
    : undefined;
  if (!Comp) {
    // Stay quiet while the bundle is still loading (keep the grid slot so others don't shift); show it in the preview if it failed to load.
    const unknown = rt.preview && rt.bundles[module] === undefined;
    if (!unknown && !l) return null;
    return (
      <div class={unknown ? 'fl-widget-error' : 'fl-widget'} style={style}>
        {unknown && rt.t.unknownWidget(w.type)}
      </div>
    );
  }
  // Shape from the design-space size: dot (small both ways), bar (short), card.
  const shape = size ? ` fl-cell fl-scaled fl-cell-${size.shape}` : '';
  return (
    <WidgetContext.Provider value={{ id: w.id, module, type: name, settings: w.settings, size }}>
      {/* Give a crashed widget another chance when its bundle changes (a new version was published). */}
      <Boundary key={rt.bundles[module]?.js} rt={rt} widget={w}>
        <div ref={el} class={`fl-widget fl-w-${module}-${name}${shape}`} data-widget={w.id} style={style}>
          <Comp />
        </div>
      </Boundary>
    </WidgetContext.Provider>
  );
}

function Zone({ rt, name }: { rt: Runtime; name: ZoneName }) {
  const list = rt.doc?.zones[name] || [];
  if (name !== 'launcher') {
    return (
      <div class={`fl-zone fl-zone-${name}`}>
        {list.map((w) => (
          <WidgetView key={w.id} rt={rt} w={w} />
        ))}
      </div>
    );
  }
  // The launcher grid is driven by CSS variables (see styles.css); the screen document can change them at any time.
  const g = normalizeLauncherGrid(rt.doc?.launcherGrid);
  const m = gridMetrics(g);
  const row = m.cellH + m.gap;
  // Visible rows fill the padded height exactly; the bottom padding (minus slack for the 1.08 focus scale) is cut so the next row does not peek out.
  const clipBottom = Math.max(0, g.padding.bottom - 10);
  const style: Record<string, string> = {
    '--fl-cols': String(m.cols),
    '--fl-cell': `${m.cell}px`,
    '--fl-cell-h': `${m.cellH}px`,
    '--fl-gap': `${m.gap}px`,
    '--fl-row': `${row}px`,
    '--fl-rows': String(m.visibleRows),
    '--fl-pt': `${g.padding.top}px`,
    '--fl-pr': `${g.padding.right}px`,
    '--fl-pb': `${g.padding.bottom}px`,
    '--fl-pl': `${g.padding.left}px`,
  };
  if (clipBottom > 0) style.clipPath = `inset(0 0 ${clipBottom}px 0)`;
  return (
    <div class="fl-zone fl-zone-launcher" style={style}>
      {list.map((w) => (
        <WidgetView key={w.id} rt={rt} w={w} m={m} />
      ))}
    </div>
  );
}

function Shell({ rt }: { rt: Runtime }) {
  const mode = useMode();
  useEffect(() => {
    if (mode === 'active') setTimeout(focusFirst, 50);
  }, [mode]);
  return (
    <>
      <Zone rt={rt} name="background" />
      <Zone rt={rt} name="launcher" />
      <Zone rt={rt} name="gallery" />
    </>
  );
}

export function App({ rt }: { rt: Runtime }) {
  rt.zoneRenderer = (name) => <Zone rt={rt} name={name as ZoneName} />;
  const [, force] = useReducer((n: number, _: void) => n + 1, 0);
  useEffect(() => {
    const off = rt.subscribeState(force);
    force(); // don't miss updates that arrived before subscribing
    return off;
  }, [rt]);

  // Theme tokens → CSS variables.
  useEffect(() => {
    const s = document.documentElement.style;
    for (const [k, v] of Object.entries(rt.doc?.theme || {})) s.setProperty(`--${k}`, v);
  }, [rt.doc?.theme]);

  return (
    <RuntimeContext.Provider value={rt}>
      {rt.doc && rt.approved ? <Shell rt={rt} /> : null}
      {!rt.approved && (
        <div class="fl-overlay">
          <div class="fl-overlay-title">{rt.t.pendingTitle}</div>
          <div class="fl-overlay-text">{rt.t.pendingText}</div>
          <div class="fl-overlay-code">{rt.deviceId}</div>
        </div>
      )}
      {!rt.doc && rt.approved && <div class="fl-overlay fl-overlay-soft">{rt.status === 'offline' ? rt.t.unreachable : rt.t.loading}</div>}
      {rt.status === 'offline' && rt.doc && <div class="fl-offline">{rt.t.offline}</div>}
      {rt.toastText && <div class="fl-toast">{rt.toastText}</div>}
    </RuntimeContext.Provider>
  );
}
